<template>
  <div class="request-demo">
      <div class="flex justify-between items-end mb-6 mdmax:flex-col mdmax:items-stretch gap-4">
        <div class="flex-1 mdmax:w-full">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label class="text-base text-neutral-700 font-medium mb-1 inline-block">Status</label>
              <v-select
                class="bg-white rounded-md"
                v-model="queries.status"
                :options="statusOptions"
                :reduce="option => option.value"
                label="label"
                placeholder="Pilih Status"
              />
            </div>
            <div>
              <label class="text-base text-neutral-700 font-medium mb-1 inline-block">Rentang Tanggal</label>
              <div class="date-range relative text-xs bg-white rounded-md">
                <date-range-picker
                  ref="picker"
                  v-model="dateRange"
                  :locale-data="{ format: 'dd/mm/yyyy', separator: ' - ' }"
                  :showDropdowns="false"
                  :timePicker="false"
                  :timePicker24Hour="false"
                  :ranges="false"
                >
                  <template v-slot:input="picker" style="min-width: 100%">
                    <span v-if="picker.startDate && picker.endDate">
                      {{ picker.startDate | date }} - {{ picker.endDate | date }}
                    </span>
                    <span v-else class="text-neutral-400">Pilih Tanggal</span>
                  </template>
                </date-range-picker>
                <div
                  v-if="dateRange.startDate && dateRange.endDate"
                  class="absolute right-2 top-1/2 transform -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-600 z-10"
                  @click.stop="clearDateLocal"
                >
                  &#x2715;
                </div>
              </div>
            </div>
            
            <div class="flex items-center gap-3 pt-7">
              <button
                @click="applyFilter"
                class="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 font-medium whitespace-nowrap"
              >
                Filter
              </button>
              <button
                v-if="hasFilter"
                @click="resetFilter"
                class="px-4 py-2 border border-neutral-300 text-neutral-600 bg-white rounded-lg hover:bg-neutral-50 font-medium whitespace-nowrap"
              >
                Reset Filter
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <TableComponent :columns="columns" :list="requestDemos">
        <template slot="table-row" slot-scope="{ entry, column }">
          <span v-if="column.field === 'action'" class="flex gap-2 justify-center">
             <button
                @click="openDetailModal(entry)"
                class="p-2 rounded bg-neutral-100 text-neutral-600 hover:bg-neutral-200 focus:outline-none"
                title="Detail Data"
             >
                <Eye class="w-4 h-4" />
             </button>
             <button
                @click="openUpdateModal(entry)"
                class="p-2 rounded bg-neutral-100 text-neutral-600 hover:bg-neutral-200 focus:outline-none"
                title="Update Status"
             >
                <Pencil class="w-4 h-4" />
             </button>
          </span>
          
          <span v-else-if="column.field === 'status'">
            <span class="px-2 py-1 rounded text-xs font-medium" :class="statusClass(entry.status)">
              {{ getStatusLabel(entry.status) }}
            </span>
          </span>

          <span v-else-if="column.field === 'created_at'">
            {{ formatDate(entry.created_at) }}
          </span>
          
          <span v-else-if="column.field === 'type'">
             {{ (entry[column.field] || '').toUpperCase() }}
          </span>
          
          <span v-else class="whitespace-nowrap">{{ entry[column.field] !== null && entry[column.field] !== undefined && entry[column.field] !== '' ? entry[column.field] : '-' }}</span>
        </template>
      </TableComponent>
      
      <div v-if="requestDemos && requestDemos.length > 0" class="mt-6 mb-6 flex justify-between items-center mdmax:flex-col mdmax:items-start gap-4">
        <div v-if="!isMobile" class="text-sm font-medium text-neutral-500">
          Menampilkan {{ start }} - {{ end }} dari {{ totalRecords }}
        </div>
        <pagination 
          :currentPage="queries.page" 
          :totalCount="totalRecords" 
          :pageSize="queries.per_page" 
          @onPageChange="onPageChange" 
        />
      </div>
    
    <Modal :modalVisible="updateModalVisible" @close="closeUpdateModal" width="450px">
      <div slot="modal-content" class="text-left">
        <h3 class="text-lg font-bold text-neutral-800 mb-4">
          Update Status
        </h3>
        <div class="mb-6">
          <label class="block text-sm font-medium text-neutral-700 mb-2">Status</label>
          <v-select
            class="bg-white rounded-md w-full"
            v-model="updateForm.status"
            :options="statusOptions.filter(o => o.value !== '')"
            :reduce="option => option.value"
            label="label"
            :clearable="false"
          />
        </div>
        <div class="flex justify-end gap-3">
          <button @click="closeUpdateModal" class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50 font-medium">Batal</button>
          <button @click="submitUpdateStatus" class="px-4 py-2 rounded-lg bg-primary-500 text-white hover:bg-primary-600 font-medium flex items-center justify-center min-w-[100px]" :disabled="isUpdating">
            <span v-if="isUpdating">Menyimpan...</span>
            <span v-else>Simpan</span>
          </button>
        </div>
      </div>
    </Modal>

    <Modal :modalVisible="detailModalVisible" @close="closeDetailModal" width="500px">
      <div slot="modal-content" class="text-left">
        <h3 class="text-lg font-bold text-neutral-800 mb-4 border-b pb-2">
          Detail Request Demo
        </h3>
        <div v-if="selectedDetail" class="space-y-4 mb-6 text-sm text-neutral-700">
          <div class="grid grid-cols-3 gap-2">
            <div class="font-medium">Nama PIC</div>
            <div class="col-span-2">{{ selectedDetail.pic_name || '-' }}</div>
          </div>
          <div class="grid grid-cols-3 gap-2">
            <div class="font-medium">Perusahaan</div>
            <div class="col-span-2">{{ selectedDetail.company || '-' }}</div>
          </div>
          <div class="grid grid-cols-3 gap-2">
            <div class="font-medium">Tipe</div>
            <div class="col-span-2">{{ selectedDetail.type ? selectedDetail.type.toUpperCase() : '-' }}</div>
          </div>
          <div class="grid grid-cols-3 gap-2">
            <div class="font-medium">No HP</div>
            <div class="col-span-2">{{ selectedDetail.phone_number || '-' }}</div>
          </div>
          <div class="grid grid-cols-3 gap-2">
            <div class="font-medium">Email</div>
            <div class="col-span-2 break-words whitespace-normal">{{ selectedDetail.email || '-' }}</div>
          </div>
          <div class="grid grid-cols-3 gap-2">
            <div class="font-medium">Alamat</div>
            <div class="col-span-2 break-words whitespace-normal">{{ selectedDetail.address || '-' }}</div>
          </div>
          <div class="grid grid-cols-3 gap-2">
            <div class="font-medium">Tanggal Dibuat</div>
            <div class="col-span-2">{{ formatDate(selectedDetail.created_at) }}</div>
          </div>
          <div class="grid grid-cols-3 gap-2">
            <div class="font-medium">Status</div>
            <div class="col-span-2">
              <span class="px-2 py-1 rounded text-xs font-medium" :class="statusClass(selectedDetail.status)">
                {{ getStatusLabel(selectedDetail.status) }}
              </span>
            </div>
          </div>
        </div>
        <div class="flex justify-end">
          <button @click="closeDetailModal" class="px-4 py-2 rounded-lg bg-neutral-200 text-neutral-700 hover:bg-neutral-300 font-medium">Tutup</button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import DateRangePicker from "vue2-daterange-picker"
import "vue2-daterange-picker/dist/vue2-daterange-picker.css"
import vSelect from "vue-select"
import "vue-select/dist/vue-select.css"
import api from "@/api"
import dayjs from "dayjs"

export default {
  components: {
    DateRangePicker,
    vSelect,
    TableComponent: () => import(/* webpackChunkName: "Table" */ "@/components/Table/Table"),
    Pagination: () => import(/* webpackChunkName: "Pagination" */ "@/components/Pagination/Pagination"),
    Modal: () => import(/* webpackChunkName: "Modal" */ "@/components/Modal/Modal"),
    Pencil: () => import(/* webpackChunkName: "Icon" */ "@/components/Icons/Pencil"),
    Eye: () => import(/* webpackChunkName: "Icon" */ "@/components/Icons/Eye")
  },
  data() {
    return {
      dateRange: {
        startDate: null,
        endDate: null
      },
      statusOptions: [
        { label: "Semua", value: "" },
        { label: "Perlu di follow up", value: "needs_follow_up" },
        { label: "Sudah di follow up", value: "followed_up" },
        { label: "Dijadwalkan demo", value: "demo_scheduled" },
        { label: "Tidak merespon", value: "not_responding" },
        { label: "Selesai", value: "completed" }
      ],
      columns: [
        { label: "Nama PIC", field: "pic_name" },
        { label: "Perusahaan", field: "company" },
        { label: "Tipe", field: "type" },
        { label: "No HP", field: "phone_number" },
        { label: "Status", field: "status" },
        { label: "Tanggal Dibuat", field: "created_at" },
        { label: "Action", field: "action" }
      ],
      queries: {
        page: 1,
        per_page: 10,
        status: "",
        start_date: "",
        end_date: ""
      },
      requestDemos: [],
      totalRecords: 0,
      
      updateModalVisible: false,
      isUpdating: false,
      updateForm: {
        id: null,
        status: ""
      },
      detailModalVisible: false,
      selectedDetail: null
    }
  },
  computed: {
    ...mapGetters("common", ["isMobile"]),
    start() {
      return this.totalRecords === 0 ? 0 : (this.queries.page - 1) * this.queries.per_page + 1
    },
    end() {
      const lastOnPage = (this.queries.page - 1) * this.queries.per_page + this.queries.per_page
      return lastOnPage > this.totalRecords ? this.totalRecords : lastOnPage
    },
    hasFilter() {
      return this.queries.status !== "" || this.queries.start_date !== ""
    }
  },
  filters: {
    date(val) {
      return val ? dayjs(val).format("DD/MM/YYYY") : ""
    }
  },
  created() {
    this.initQueries()
    this.fetchData()
  },
  watch: {
    $route(to, from) {
      if (to.query !== from.query) {
        this.initQueries()
        this.fetchData()
      }
    }
  },
  methods: {
    ...mapActions("common", ["showLoading", "hideLoading"]),
    
    initQueries() {
      this.queries.page = this.$route.query.page ? parseInt(this.$route.query.page) : 1
      this.queries.status = this.$route.query.status || ""
      
      if (this.$route.query.start_date && this.$route.query.end_date) {
        this.queries.start_date = this.$route.query.start_date
        this.queries.end_date = this.$route.query.end_date
        this.dateRange = {
          startDate: dayjs(this.$route.query.start_date).toDate(),
          endDate: dayjs(this.$route.query.end_date).toDate()
        }
      } else {
        this.queries.start_date = ""
        this.queries.end_date = ""
        this.dateRange = { startDate: null, endDate: null }
      }
    },
    
    async fetchData() {
      this.showLoading()
      try {
        const response = await api.requestDemo.list(this.queries)
        const data = response.data?.data
        this.requestDemos = data?.items || []
        this.totalRecords = data?.pagination?.total_data || 0
      } catch (error) {
        this.$toast?.error(error.response?.data?.message || "Gagal mengambil data")
      } finally {
        this.hideLoading()
      }
    },
    
    applyFilter() {
      if (this.dateRange.startDate && this.dateRange.endDate) {
        this.queries.start_date = dayjs(this.dateRange.startDate).format("YYYY-MM-DD")
        this.queries.end_date = dayjs(this.dateRange.endDate).format("YYYY-MM-DD")
      } else {
        this.queries.start_date = ""
        this.queries.end_date = ""
      }
      this.doSearch()
    },
    
    resetFilter() {
      this.queries.status = ""
      this.queries.start_date = ""
      this.queries.end_date = ""
      this.dateRange = { startDate: null, endDate: null }
      this.doSearch()
    },
    
    clearDateLocal() {
      this.dateRange = { startDate: null, endDate: null }
    },
    
    openUpdateModal(entry) {
      this.updateForm.id = entry.id
      this.updateForm.status = entry.status
      this.updateModalVisible = true
    },
    
    closeUpdateModal() {
      this.updateModalVisible = false
      this.updateForm = { id: null, status: "" }
    },
    
    openDetailModal(entry) {
      this.selectedDetail = entry
      this.detailModalVisible = true
    },
    
    closeDetailModal() {
      this.detailModalVisible = false
      this.selectedDetail = null
    },
    
    async submitUpdateStatus() {
      if (!this.updateForm.id || !this.updateForm.status) return
      
      this.isUpdating = true
      try {
        await api.requestDemo.updateStatus(this.updateForm.id, { status: this.updateForm.status })
        this.$toast?.success("Status berhasil diperbarui")
        this.fetchData()
        this.closeUpdateModal()
      } catch (error) {
        this.$toast?.error(error.response?.data?.message || "Gagal memperbarui status")
      } finally {
        this.isUpdating = false
      }
    },
    
    doSearch() {
      const query = { ...this.queries, page: 1 }
      // Remove empty queries
      Object.keys(query).forEach(key => {
        if (query[key] === "" || query[key] === null || query[key] === undefined) {
          delete query[key]
        }
      })
      this.$router.push({ path: this.$route.path, query }).catch(()=>{})
    },
    
    onPageChange(page) {
      const query = { ...this.$route.query, page }
      this.$router.push({ path: this.$route.path, query }).catch(()=>{})
    },
    
    getStatusLabel(status) {
      const found = this.statusOptions.find(opt => opt.value === status)
      return found ? found.label : status
    },
    
    statusClass(status) {
      switch (status) {
        case 'needs_follow_up':
          return 'bg-red-100 text-red-700'
        case 'followed_up':
          return 'bg-blue-100 text-blue-700'
        case 'demo_scheduled':
          return 'bg-purple-100 text-purple-700'
        case 'not_responding':
          return 'bg-gray-200 text-gray-700'
        case 'completed':
          return 'bg-green-100 text-green-700'
        default:
          return 'bg-gray-100 text-gray-700'
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '-'
      return dayjs(dateStr).format('DD MMM YYYY HH:mm')
    }
  }
}
</script>

<style scoped>
.date-range >>> .vue-daterange-picker {
  width: 100%;
}
.date-range >>> .reportrange-text {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  line-height: 1.5;
  min-height: 38px;
  display: flex;
  align-items: center;
}
.request-demo >>> .v-select .vs__dropdown-toggle {
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  padding: 0.25rem 0.5rem;
  min-height: 38px;
}
.request-demo >>> .v-select .vs__search::placeholder,
.request-demo >>> .v-select .vs__dropdown-toggle,
.request-demo >>> .v-select .vs__dropdown-menu {
  color: #374151;
}
</style>
